import React, { useState, useRef } from 'react';
import { ReactComponent as WarningIcon } from '../../../assets/images/warning-triangle-icon.svg';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { InputText } from 'primereact/inputtext';
import { ProductService } from '../../../service/ProductService';
import './AddProductDialog.scss';

const AddProductDialog = ({ visible, onHide, onAdd, onSkip }) => {
    const { t } = useTranslation();
    const [eanCode, setEanCode] = useState(null);
    const [isEanAvailable, setIsEanAvailable] = useState(true);
    const [isCheckingEan, setIsCheckingEan] = useState(false);
    const eanInputRef = useRef(null);
    const addButtonRef = useRef(null);

    const handleAdd = async () => {
        if (eanCode && eanCode.toString().trim()) {
            setIsCheckingEan(true);
            try {
                const response = await ProductService.checkEanAvailability(eanCode.toString().trim());
                if (response) {
                    onAdd(eanCode.toString().trim());
                    setEanCode(null);
                    setIsEanAvailable(true);
                } else {
                    setIsEanAvailable(false);
                }
            } catch (error) {
                console.error('Error checking EAN availability:', error);
                onAdd(eanCode.toString().trim());
                setEanCode(null);
                setIsEanAvailable(true);
            } finally {
                setIsCheckingEan(false);
            }
        }
    };

    const handleSkip = () => {
        onSkip();
        setEanCode(null);
        setIsEanAvailable(true);
    };

    const handleHide = () => {
        setEanCode(null);
        setIsEanAvailable(true);
        onHide();
    };

    return (
        <Dialog
            visible={visible}
            onHide={handleHide}
            header={t('products.addNewProduct')}
            className="add-product-dialog"
            closable={true}
            closeOnEscape={true}
            modal={true}
            draggable={false}
            resizable={false}
            focusOnShow={true}
            autoFocus={true}
            onShow={() => {
                if (eanInputRef.current) {
                    eanInputRef.current.focus();
                }
            }}
            footer={
                <div className="flex w-full gap-3 justify-content-end">
                    <Button
                        ref={addButtonRef}
                        label={t('common.add')}
                        onClick={handleAdd}
                        disabled={!eanCode || !eanCode.toString().trim() || isCheckingEan}
                        className='w-full'
                    />
                    <Button
                        label={t('common.skip')}
                        onClick={handleSkip}
                        className="w-full p-button-secondary"
                    />
                </div>
            }
        >
            <div className="flex flex-column gap-5">

                {!isEanAvailable && eanCode && eanCode.toString().trim() && (
                    <div className="flex align-items-center gap-2">
                        <WarningIcon className="text-orange-500" />
                        <div className="flex flex-column">
                            <span className="text-sm text-orange-500">
                                <span className="font-semibold">{t('products.eanWarning.warning')} </span>
                                {t('products.eanWarning.message')}
                            </span>
                        </div>
                    </div>
                )}

                <p className="text-color-secondary text-sm line-height-3 m-0">
                    {t('products.scanEanDescription')}
                </p>


                <div className="flex flex-column gap-2">
                    <label htmlFor="eanCode" className="font-semibold text-color-secondary text-sm ">
                        {t('products.eanCode')}
                    </label>
                    <InputText
                        ref={eanInputRef}
                        id="eanCode"
                        value={eanCode ?? ''}
                        onChange={(e) => {
                            setEanCode(e.target.value);
                            // Reset warning when EAN changes
                            if (!isEanAvailable) {
                                setIsEanAvailable(true);
                            }
                        }}
                        placeholder="12356789343"
                        className="w-full"
                        keyfilter="int"
                        pattern="\\d*"
                        inputMode="numeric"
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                if (addButtonRef.current && !addButtonRef.current.disabled) {
                                    addButtonRef.current.click();
                                }
                            }
                        }}
                        maxLength={64}
                    />
                    {isCheckingEan && (
                        <small className="text-color-secondary">
                            <i className="pi pi-spin pi-spinner mr-1"></i>
                            {t('products.checkingEan')}
                        </small>
                    )}
                </div>
            </div>
        </Dialog>
    );
};

export default AddProductDialog;
